/**
 * CompanyForm — the four-section form on Settings → Company profile.
 *
 * Mirrors `prototype/settings/settings-company.html ▸ form.set-pane`:
 *
 *   1. Identity            — legalName · tradeName · email · phone
 *   2. Brand               — logo (S3 upload) + accent swatch picker
 *   3. Registered address  — address · city · state · pincode · country
 *   4. Registration & finance — pan · type · business · cin · registeredOn ·
 *                              fy · currency · verification chips
 *
 * State + dirty tracking lives here; the SaveBar is rendered by the route
 * (so the bar state stays paired with the form). PAN, CIN, GSTIN render
 * as "Locked" inputs once they have a value (the wire schema strips them
 * from the patch in that case; the server enforces it as the floor).
 *
 * @see prototype/settings/settings-company.html — visual reference
 * @see ../../company/lib/form-defaults.ts — values <→ wire seam
 */
import * as React from "react";
import {
  Building2,
  Palette,
  MapPin,
  Shield,
  Lock,
  CheckCircle,
  AlertCircle,
  Landmark,
  Ship,
} from "lucide-react";
import {
  Alert,
  Input,
  Label,
  StatusPill,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  cn,
} from "@flint/ui";
import { indianState, companies as companiesContract } from "@flint/api-contract";
import { deriveInitials } from "../../../lib/format.ts";
import { FormCard } from "../form-card.tsx";

// Wire enums double as the form's option lists. Zod's `.options` returns
// the underlying string array — same source of truth as the server, no
// duplication. Domain-side enum constants stay server-side per the
// architecture rule (apps/web depends only on api-contract, not domain).
const ACCENT_COLOURS = companiesContract.accentColour.options;
type AccentColour = (typeof ACCENT_COLOURS)[number];

/** Type guard for narrowing legacy/free-form values on read into the enum. */
function isAccentColour(v: string): v is AccentColour {
  return (ACCENT_COLOURS as readonly string[]).includes(v);
}
import { AccentSwatchPicker } from "./accent-swatch-picker.tsx";
import { LogoUploader, logoStatusMessage, type LogoUploadStatus } from "./logo-uploader.tsx";
import { EMPTY_FORM, type CompanyFormValues } from "./lib/form-defaults.ts";

const STATE_OPTIONS = indianState.options;

export interface CompanyFormProps {
  /** Hydrated form values (controlled by the route). */
  values: CompanyFormValues;
  /**
   * Last-saved server snapshot. Used by the form to:
   *   - lock the PAN/CIN/GSTIN inputs once they've been set server-side
   *     (any non-empty baseline value flips the field to read-only).
   *
   * Without this prop the form would compute lockedness from `values`,
   * which means the input would lock on the first character the user
   * typed for a first-time entry — preventing PAN/CIN/GSTIN from ever
   * being recorded. Pass the unmodified hydrated row.
   */
  baseline: CompanyFormValues;
  /** Logo public URL (or null). Updated by the LogoUploader's invalidation. */
  logoUrl: string | null;
  /**
   * Field-level errors keyed by `CompanyFormValues` key. Currently used
   * for the locked-field error (e.g. `{ pan: 'PAN is locked …' }`).
   */
  fieldErrors?: Partial<Record<keyof CompanyFormValues, string>>;
  /**
   * Submit-time error banner, rendered above the cards. The route's
   * mutation passes this when `update` rejects.
   */
  serverError?: string | null;
  /**
   * When true, the whole form renders view-only — every editable input is
   * `readOnly`, Selects and pickers are disabled, and the caller is
   * expected to hide the SaveBar. Server-enforced permission gate:
   * `permissionGate('settings.manage')` in the router; this prop is UX
   * softening for members without the grant (ADR-0030).
   */
  readOnly?: boolean;
  onChange: (next: CompanyFormValues) => void;
}

/**
 * Pure controlled form. The route owns the values + dirty tracking;
 * this component only renders the inputs and bubbles changes up.
 *
 * Why not RHF: the form is largely text + select + a couple of pickers,
 * and the route already manages the wire round-trip + the SaveBar's
 * dirty state. RHF would add a layer without earning it; the form
 * stays simpler as a single React state object.
 */
export function CompanyForm({
  values,
  baseline,
  logoUrl,
  fieldErrors,
  serverError,
  readOnly = false,
  onChange,
}: CompanyFormProps) {
  // Patch helper: produce a partial change while preserving the rest.
  const patch = React.useCallback(
    <K extends keyof CompanyFormValues>(key: K, next: CompanyFormValues[K]) => {
      onChange({ ...values, [key]: next });
    },
    [values, onChange],
  );

  const [logoStatus, setLogoStatus] = React.useState<LogoUploadStatus>({ kind: "idle" });
  const logoLine = logoStatusMessage(logoStatus);

  // Locked-once-set: derive from BASELINE (server-side state), not from
  // `values` (live form). If we used `values`, the input would flip to
  // read-only the moment a user typed their first character on a fresh
  // company — preventing first-time PAN/CIN entry entirely.
  // GSTIN was moved to /settings/gstin and is shown read-only here.
  const panLocked = baseline.pan.length > 0;
  const cinLocked = baseline.cin.length > 0;
  // Cascade the workspace-wide permission gate into per-field lock flags.
  // Every editable input asks a single question ("is my field locked?"),
  // and the answer already blends the PAN/CIN "locked once set" rule with
  // the view-only mode.
  const lockedByRole = readOnly;
  const panReadOnly = panLocked || lockedByRole;
  const cinReadOnly = cinLocked || lockedByRole;

  // Initials for the LogoUploader empty state — derived from legalName.
  // Lives here (not the route) so the form is self-sufficient given just
  // values + baseline.
  const logoInitials = deriveInitials(values.legalName);

  return (
    <>
      {serverError ? (
        <Alert intent="bad" title="Couldn't save company profile" description={serverError} />
      ) : null}

      {/* ── Identity ─────────────────────────────────────────────── */}
      <FormCard
        id="identity"
        icon={<Building2 size={14} strokeWidth={2} aria-hidden />}
        title="Identity"
        description="Used on every invoice, bill, email, and audit-trail entry."
        badge={
          <StatusPill tone="sky" size="xs">
            Visible to customers
          </StatusPill>
        }
      >
        <FieldGrid>
          {/* Legal name (75%) + Trade name (25%) share one row via a 4-col sub-grid. */}
          <div className="grid grid-cols-1 gap-4 sm:col-span-2 sm:grid-cols-4">
            <Field
              label="Legal name"
              labelFor="company-legal-name"
              required
              colSpanClass="sm:col-span-3"
              error={fieldErrors?.legalName}
            >
              <Input
                id="company-legal-name"
                value={values.legalName}
                maxLength={255}
                autoComplete="organization"
                readOnly={readOnly}
                onChange={(e) => patch("legalName", e.target.value)}
              />
              <Hint>Exactly as it appears on your PAN and GST registration certificates.</Hint>
            </Field>
            <Field
              label="Trade name"
              labelFor="company-trade-name"
              colSpanClass="sm:col-span-1"
              error={fieldErrors?.tradeName}
            >
              <Input
                id="company-trade-name"
                value={values.tradeName}
                maxLength={255}
                readOnly={readOnly}
                onChange={(e) => patch("tradeName", e.target.value)}
              />
              <Hint>Customer-facing short name. Used in WhatsApp and email subjects.</Hint>
            </Field>
          </div>
          <Field
            label="Registered email"
            labelFor="company-email"
            required
            error={fieldErrors?.email}
          >
            <Input
              id="company-email"
              type="email"
              value={values.email}
              autoComplete="email"
              maxLength={255}
              readOnly={readOnly}
              onChange={(e) => patch("email", e.target.value)}
            />
            <Hint>Where statutory notices and Flint alerts are sent.</Hint>
          </Field>
          <Field
            label="Primary business phone"
            labelFor="company-phone"
            error={fieldErrors?.phone}
          >
            <Input
              id="company-phone"
              type="tel"
              value={values.phone}
              autoComplete="tel"
              inputMode="tel"
              maxLength={16}
              className="font-mono"
              readOnly={readOnly}
              onChange={(e) => patch("phone", e.target.value)}
            />
            <Hint>Different from your founder phone — this is what customers see.</Hint>
          </Field>
        </FieldGrid>
      </FormCard>

      {/* ── Brand ────────────────────────────────────────────────── */}
      <FormCard
        id="brand"
        icon={<Palette size={14} strokeWidth={2} aria-hidden />}
        title="Brand"
        description="Logo and accent colour used on customer-facing documents and workspace branding."
      >
        <FieldGrid>
          <Field label="Logo" full>
            <div className="flex items-start gap-4">
              <LogoUploader
                currentUrl={logoUrl}
                initials={logoInitials}
                readOnly={readOnly}
                onStatusChange={setLogoStatus}
              />
              <div className="flex flex-col gap-1 text-sm">
                <p className={cn("text-xs", logoLine.tone === "bad" ? "text-bad" : "text-muted")}>
                  {logoLine.text}
                </p>
                <p className="text-xs text-muted">
                  Click the logo to upload. Flint auto-fits the image to invoice header height.
                </p>
              </div>
            </div>
          </Field>
          <Field label="Accent colour" full>
            <AccentSwatchPicker
              value={isAccentColour(values.accentColour) ? values.accentColour : "sky"}
              disabled={readOnly}
              onChange={(next) => patch("accentColour", next)}
            />
            <Hint>
              Used for invoice header bar, link colour, and the lens dot in your logo mark.
            </Hint>
          </Field>
        </FieldGrid>
      </FormCard>

      {/* ── Registered address ───────────────────────────────────── */}
      <FormCard
        id="address"
        icon={<MapPin size={14} strokeWidth={2} aria-hidden />}
        title="Registered address"
        description="The address on your GST certificate. Branch addresses live under GSTIN & branches."
      >
        <FieldGrid>
          <Field
            label="Street address"
            labelFor="company-address"
            required
            full
            error={fieldErrors?.address}
          >
            <textarea
              id="company-address"
              rows={4}
              value={values.address}
              autoComplete="street-address"
              maxLength={2048}
              readOnly={readOnly}
              onChange={(e) => patch("address", e.target.value)}
              className="w-full px-3 py-2 text-sm text-ink bg-paper border border-line rounded-sm placeholder:text-muted focus-visible:outline-none focus-visible:border-sky-600 focus-visible:ring-3 focus-visible:ring-sky-100 resize-y min-h-[104px]"
            />
          </Field>
          <Field label="City" labelFor="company-city" error={fieldErrors?.city}>
            <Input
              id="company-city"
              value={values.city}
              autoComplete="address-level2"
              maxLength={64}
              readOnly={readOnly}
              onChange={(e) => patch("city", e.target.value)}
            />
          </Field>
          <Field label="State" labelFor="company-state" required error={fieldErrors?.state}>
            <Select
              {...(values.state ? { value: values.state } : {})}
              disabled={readOnly}
              onValueChange={(v) => patch("state", v)}
            >
              <SelectTrigger id="company-state">
                <SelectValue placeholder="Select state" />
              </SelectTrigger>
              <SelectContent>
                {STATE_OPTIONS.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Hint>Determines place of supply for B2B invoices issued from this branch.</Hint>
          </Field>
          <Field label="PIN code" labelFor="company-pincode" error={fieldErrors?.pincode}>
            <Input
              id="company-pincode"
              value={values.pincode}
              inputMode="numeric"
              maxLength={6}
              className="font-mono"
              readOnly={readOnly}
              onChange={(e) => patch("pincode", e.target.value.replace(/\D/g, ""))}
            />
          </Field>
          <Field label="Country" labelFor="company-country">
            <Input
              id="company-country"
              value="India"
              readOnly
              className="bg-bg cursor-not-allowed"
            />
            <Hint>India-only during V1.</Hint>
          </Field>
        </FieldGrid>
      </FormCard>

      {/* ── Registration & finance ───────────────────────────────── */}
      <FormCard
        id="registration"
        icon={<Shield size={14} strokeWidth={2} aria-hidden />}
        title="Registration & finance"
        description="Statutory identifiers and your fiscal calendar. PAN, CIN, and GSTIN are locked once entered — contact support to change."
      >
        <FieldGrid>
          <Field label="PAN" labelFor="company-pan" lockBadge={panLocked} error={fieldErrors?.pan}>
            <Input
              id="company-pan"
              value={values.pan}
              maxLength={10}
              readOnly={panReadOnly}
              autoCapitalize="characters"
              autoComplete="off"
              spellCheck={false}
              className={cn(
                "font-mono uppercase tracking-wide",
                panReadOnly && "bg-bg cursor-not-allowed",
              )}
              onChange={(e) =>
                patch(
                  "pan",
                  e.target.value
                    .toUpperCase()
                    .replace(/[^A-Z0-9]/g, "")
                    .slice(0, 10),
                )
              }
            />
            <Hint>Drives TDS, vendor matching, and GST verification. ABCDE1234F format.</Hint>
          </Field>

          <Field label="Business type" labelFor="company-type">
            <Select
              {...(values.type ? { value: values.type } : {})}
              disabled={readOnly}
              onValueChange={(v) => patch("type", v)}
            >
              <SelectTrigger id="company-type">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Regular">Regular</SelectItem>
                <SelectItem value="Composition">Composition</SelectItem>
                <SelectItem value="SEZ">SEZ</SelectItem>
                <SelectItem value="Casual">Casual</SelectItem>
              </SelectContent>
            </Select>
          </Field>

          <Field label="CIN" labelFor="company-cin" lockBadge={cinLocked} error={fieldErrors?.cin}>
            <Input
              id="company-cin"
              value={values.cin}
              maxLength={21}
              readOnly={cinReadOnly}
              autoCapitalize="characters"
              autoComplete="off"
              spellCheck={false}
              className={cn(
                "font-mono uppercase tracking-wide",
                cinReadOnly && "bg-bg cursor-not-allowed",
              )}
              onChange={(e) =>
                patch(
                  "cin",
                  e.target.value
                    .toUpperCase()
                    .replace(/[^A-Z0-9]/g, "")
                    .slice(0, 21),
                )
              }
            />
            <Hint>21-char identifier from the MCA. Required for Pvt Ltd / Public Ltd / OPC.</Hint>
          </Field>

          <Field label="Registered on" labelFor="company-registered-on">
            <Input
              id="company-registered-on"
              type="date"
              value={values.registeredOn}
              readOnly={readOnly}
              onChange={(e) => patch("registeredOn", e.target.value)}
            />
            <Hint>Printed on the invoice footer for Pvt Ltd / Public Ltd / OPC entities.</Hint>
          </Field>

          <Field label="Reporting currency" labelFor="company-currency">
            <Input
              id="company-currency"
              value="Indian Rupee (₹ INR)"
              readOnly
              className="bg-bg cursor-not-allowed"
            />
          </Field>

          <Field label="GSTIN" labelFor="company-gstin-display" full>
            <Input
              id="company-gstin-display"
              value={values.gstin || "Not set"}
              readOnly
              className={cn(
                "font-mono uppercase tracking-wide bg-bg cursor-not-allowed",
                !values.gstin && "text-muted normal-case",
              )}
            />
            <Hint>
              Edit on the{" "}
              <a
                className="inline-flex min-h-11 items-center rounded-sm px-1 text-sky-700 underline"
                href="/settings/gstin"
              >
                branches page
              </a>{" "}
              — that page also controls registration type and place-of-supply default.
            </Hint>
          </Field>

          <Field label="Verification" full>
            <div className="flex flex-wrap gap-1.5 mt-1">
              <VerificationChip
                tone={values.pan ? "ok" : "muted"}
                label={values.pan ? "PAN recorded" : "PAN not set"}
              />
              <VerificationChip
                tone={values.gstin ? "ok" : "muted"}
                label={values.gstin ? "GSTIN active" : "GSTIN not set"}
              />
              <VerificationChip
                tone={values.cin ? "info" : "muted"}
                label={values.cin ? "CIN recorded" : "CIN not set"}
              />
              <VerificationChip tone="warn" label="e-Invoice IRN — connect (V1.x)" />
            </div>
            <Hint>
              External MCA / GSTN verification lands when those integrations connect. For now we
              surface what we&apos;ve recorded.
            </Hint>
          </Field>
        </FieldGrid>
      </FormCard>

      {/* ── Bank details (printed on invoices) ───────────────────── */}
      <FormCard
        id="bank-details"
        icon={<Landmark size={14} strokeWidth={2} aria-hidden />}
        title="Bank details"
        description="Printed on the customer-facing invoice and receipt as the “Pay to” block. UPI takes priority — IFSC + account number are the NEFT/RTGS fallback."
        badge={
          <StatusPill tone="sky" size="xs">
            Visible to customers
          </StatusPill>
        }
      >
        <FieldGrid>
          <Field label="Bank name" labelFor="company-bank-name" full error={fieldErrors?.bankName}>
            <Input
              id="company-bank-name"
              value={values.bankName}
              maxLength={64}
              placeholder="e.g. Canara Bank · Peenya Branch"
              readOnly={readOnly}
              onChange={(e) => patch("bankName", e.target.value)}
            />
          </Field>
          <Field
            label="Account number"
            labelFor="company-bank-account-number"
            error={fieldErrors?.bankAccountNumber}
          >
            <Input
              id="company-bank-account-number"
              value={values.bankAccountNumber}
              inputMode="numeric"
              maxLength={18}
              className="font-mono"
              placeholder="6–18 digits"
              readOnly={readOnly}
              onChange={(e) =>
                patch("bankAccountNumber", e.target.value.replace(/\D/g, "").slice(0, 18))
              }
            />
            <Hint>Printed in full on the invoice — the customer needs it to NEFT.</Hint>
          </Field>
          <Field label="IFSC" labelFor="company-bank-ifsc" error={fieldErrors?.bankIfsc}>
            <Input
              id="company-bank-ifsc"
              value={values.bankIfsc}
              maxLength={11}
              autoCapitalize="characters"
              spellCheck={false}
              className="font-mono uppercase tracking-wide"
              placeholder="ABCD0XXXXXX"
              readOnly={readOnly}
              onChange={(e) =>
                patch(
                  "bankIfsc",
                  e.target.value
                    .toUpperCase()
                    .replace(/[^A-Z0-9]/g, "")
                    .slice(0, 11),
                )
              }
            />
            <Hint>11-char RBI code: 4 letters · 0 · 6 alphanumerics.</Hint>
          </Field>
          <Field label="UPI ID" labelFor="company-bank-upi" full error={fieldErrors?.bankUpiId}>
            <Input
              id="company-bank-upi"
              value={values.bankUpiId}
              maxLength={64}
              autoCapitalize="none"
              spellCheck={false}
              className="font-mono"
              placeholder="loomwell.textiles@okhdfcbank"
              readOnly={readOnly}
              onChange={(e) => patch("bankUpiId", e.target.value.trim())}
            />
            <Hint>Shown as the first “Pay to” option on the invoice — one-tap pay for customers.</Hint>
          </Field>
        </FieldGrid>
      </FormCard>

      {/* ── Export credentials ───────────────────────────────────── */}
      <FormCard
        id="export-credentials"
        icon={<Ship size={14} strokeWidth={2} aria-hidden />}
        title="Export credentials"
        description="Statutory identifiers required on export paperwork (shipping bill, export invoice, BRC). Set once; referenced across the garment export flow."
      >
        <FieldGrid>
          <Field label="IEC (Import Export Code)" labelFor="company-iec" error={fieldErrors?.iec}>
            <Input
              id="company-iec"
              value={values.iec}
              maxLength={10}
              autoCapitalize="characters"
              spellCheck={false}
              className="font-mono uppercase tracking-wide"
              placeholder="ABCDE1234F"
              readOnly={readOnly}
              onChange={(e) => patch("iec", e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10))}
            />
            <Hint>10-char DGFT code — issued once per business.</Hint>
          </Field>
          <Field label="AD code (export bank)" labelFor="company-ad-code" error={fieldErrors?.adCode}>
            <Input
              id="company-ad-code"
              value={values.adCode}
              maxLength={20}
              spellCheck={false}
              className="font-mono"
              placeholder="6280005-0400010"
              readOnly={readOnly}
              onChange={(e) => patch("adCode", e.target.value.trim())}
            />
            <Hint>Authorised-Dealer code of your export bank branch.</Hint>
          </Field>
          <Field label="AEPC / RCMC no." labelFor="company-aepc" error={fieldErrors?.aepcRcmcNo}>
            <Input
              id="company-aepc"
              value={values.aepcRcmcNo}
              maxLength={40}
              spellCheck={false}
              className="font-mono"
              placeholder="AEPC/…/RCMC"
              readOnly={readOnly}
              onChange={(e) => patch("aepcRcmcNo", e.target.value.trim())}
            />
            <Hint>Apparel Export Promotion Council registration.</Hint>
          </Field>
          <Field label="LUT no. (ARN)" labelFor="company-lut-no" error={fieldErrors?.lutNo}>
            <Input
              id="company-lut-no"
              value={values.lutNo}
              maxLength={40}
              spellCheck={false}
              className="font-mono"
              placeholder="AD……"
              readOnly={readOnly}
              onChange={(e) => patch("lutNo", e.target.value.trim())}
            />
            <Hint>LUT ARN — zero-rated export without IGST. Expiry hygiene tracked separately.</Hint>
          </Field>
        </FieldGrid>
      </FormCard>

      {/* ── Invoice defaults ─────────────────────────────────────── */}
      <FormCard
        id="invoice-defaults"
        icon={<Shield size={14} strokeWidth={2} aria-hidden />}
        title="Invoice defaults"
        description="Boilerplate text that prefills on every invoice you create. Operators can override per invoice in the composer."
      >
        <FieldGrid>
          <Field
            label="Default Declaration"
            labelFor="company-default-invoice-declaration"
            full
            error={fieldErrors?.defaultInvoiceDeclaration}
          >
            <textarea
              id="company-default-invoice-declaration"
              value={values.defaultInvoiceDeclaration}
              maxLength={4000}
              rows={5}
              readOnly={readOnly}
              onChange={(e) => patch("defaultInvoiceDeclaration", e.target.value)}
              placeholder={
                "One declaration per line, e.g.:\n" +
                "We hereby declare that this invoice is issued for export of services under LUT without payment of IGST, as per Section 16 of the IGST Act, 2017.\n" +
                "Payment will be received in convertible foreign exchange.\n" +
                "The services are delivered outside India and qualify as Export of Services."
              }
              className="w-full px-3 py-2 text-sm rounded-md border border-line bg-paper focus:outline-none focus:ring-1 focus:ring-line resize-y font-mono"
            />
            <Hint>
              One statement per line — the PDF renders these as a numbered list. Leave empty to use
              Flint&apos;s built-in Section-16 LUT block on export invoices, or no declaration at
              all on domestic.
            </Hint>
          </Field>
        </FieldGrid>
      </FormCard>
    </>
  );
}

// ── Local primitives ────────────────────────────────────────────────

function FieldGrid({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>;
}

interface FieldProps {
  label: string;
  labelFor?: string | undefined;
  required?: boolean | undefined;
  full?: boolean | undefined;
  /** Override the column span (e.g. inside a sub-grid). Wins over `full`. */
  colSpanClass?: string | undefined;
  lockBadge?: boolean | undefined;
  error?: string | undefined;
  children: React.ReactNode;
}

function Field({ label, labelFor, required, full, colSpanClass, lockBadge, error, children }: FieldProps) {
  return (
    <div className={cn("flex flex-col gap-1.5", colSpanClass ?? (full && "sm:col-span-2"))}>
      <Label {...(labelFor ? { htmlFor: labelFor } : {})} className="flex items-center gap-1.5">
        <span>{label}</span>
        {required ? (
          <span className="text-bad" aria-hidden>
            *
          </span>
        ) : null}
        {lockBadge ? (
          <span className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-muted">
            <Lock size={9} strokeWidth={2.5} aria-hidden />
            Locked
          </span>
        ) : null}
      </Label>
      {children}
      {error ? <p className="text-xs text-bad">{error}</p> : null}
    </div>
  );
}

function Hint({ children }: { children: React.ReactNode }) {
  return <p className="text-xs text-muted leading-relaxed">{children}</p>;
}

function VerificationChip({
  tone,
  label,
}: {
  tone: "ok" | "warn" | "info" | "muted";
  label: string;
}) {
  const palette =
    tone === "ok"
      ? "bg-ok-50 text-ok border-ok-100"
      : tone === "warn"
        ? "bg-warn-50 text-warn border-warn-100"
        : tone === "info"
          ? "bg-sky-50 text-sky-700 border-sky-100"
          : "bg-bg text-muted border-line";
  const Icon = tone === "ok" ? CheckCircle : AlertCircle;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm text-xs font-medium border",
        palette,
      )}
    >
      <Icon size={10} strokeWidth={2.5} aria-hidden />
      {label}
    </span>
  );
}

// Re-export defaults for the route to consume.
export { EMPTY_FORM };
export type { CompanyFormValues };
